import {
  Box3,
  BoxGeometry,
  CylinderGeometry,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  JOINT_TYPE,
  MODEL_PART_TYPE,
} from "../core/configurator/ConfiguratorData";
import { isObbDebugDrawEnabled } from "../util/DebugFlags";
import { disposeObject3DDeep, transformObb } from "../util/GeometryUtil";
import {
  alignJoints,
  bindModelsByJointSpec,
  fetchMinDistance,
  getPartTypeMatch,
  getPossibleBindableJoints,
  realignJoints,
} from "../util/JointUtil";
import { logger } from "../util/Logger";
import { logConfiguratorSlotState } from "../util/logConfiguratorSlotState";
import type { HistoryDir, SugarStateSlice } from "../history/SugarObject";
import { SugarModel } from "./SugarModel";
import { SugarModelGroup } from "./SugarModelGroup";
import { SugarModelJoint } from "./SugarModelJoint";
import { createMaterial } from "./SugarModelLoader";

type ConfiguratorEvent = {
  baseJoint: SugarModelJoint;
  tailJoint: SugarModelJoint;
  operations?: any[];
  models: SugarModel[];
};

type SideJointSlot = {
  joint: SugarModelJoint;
  data: JOINT_TYPE;
};

type LoadedSidePart = {
  data: JOINT_TYPE;
  sideModel: SugarModel;
};

type LoadedMaterial = {
  code: string;
  material: ReturnType<typeof createMaterial>;
};

type PreloadedAssets = {
  sideModels: LoadedSidePart[];
  materials: LoadedMaterial[];
  mergeEvents: ConfiguratorEvent[];
};

type ConfiguratorStateValue = {
  model: SugarStateSlice["value"];
  mainConfig: Array<[string, MODEL_PART_TYPE]>;
  sideConfig: Array<[string, MODEL_PART_TYPE]>;
  materialConfig: Array<[string, { code: string; material: any }]>;
};

function obbCorner(obb: OBB, sx: -1 | 1, sy: -1 | 1, sz: -1 | 1): Vector3 {
  return new Vector3(
    sx * obb.halfSize.x,
    sy * obb.halfSize.y,
    sz * obb.halfSize.z,
  )
    .applyMatrix3(obb.rotation)
    .add(obb.center);
}

export class SugarModelConfigurator extends SugarModel {
  private _models: SugarModel[] = [];
  private _sideModels: SugarModel[] = [];

  private _unbindedJoints: SugarModelJoint[] = [];
  private _sideConfig: Map<string, MODEL_PART_TYPE> = new Map();
  private _mainConfig: Map<string, MODEL_PART_TYPE> = new Map();

  private _boundDebug?: LineSegments;
  private _childObbDebug: LineSegments[] = [];
  private _jointsDebug: Mesh[] = [];
  private _centerCylDebug?: Mesh;
  private _debugRafId?: number;

  private _events: ConfiguratorEvent[] = [];
  private _mergeEventLoadCache = new Map<
    string,
    Promise<ConfiguratorEvent | undefined>
  >();

  get materialConfig() {
    return this._materialConfig;
  }

  get mainConfig() {
    return this._mainConfig;
  }

  get sideConfig() {
    return this._sideConfig;
  }

  get sideModels() {
    return this._sideModels;
  }

  /** Remove auto-placed kolçaklar (refs kept unless forget). */
  clearSideParts(forget = false): void {
    for (const part of this._sideModels) {
      part.freeze = false;
      part.removeFromParent();
    }
    if (forget) this._sideModels = [];
  }

  /**
   * Reuse existing side models on current empty joints (sync).
   * No load / material — for center after parent origin shift.
   */
  realignSideParts(): void {
    this._sidePartsInstallGeneration++;

    for (const part of this._sideModels) {
      part.freeze = false;
      part.removeFromParent();
    }

    const pool = this._sideModels;
    const usedIndexes = new Set<number>();
    const alignedModels: SugarModel[] = [];

    for (const emptyJointSlot of this._getEmptyJoints()) {
      const modelConfig = this.getSideModelConfig(emptyJointSlot.data);
      if (!modelConfig) continue;

      const poolIndex = pool.findIndex(
        (sideModel, index) =>
          !usedIndexes.has(index) && sideModel.data.id === modelConfig.id,
      );
      if (poolIndex < 0) continue;
      usedIndexes.add(poolIndex);

      if (this.alignAndAttachSidePart(emptyJointSlot, pool[poolIndex])) {
        alignedModels.push(pool[poolIndex]);
      }
    }

    this._sideModels = alignedModels;
    this.recomputeBound();
  }

  /** Align → material → bound. Shared by refresh + changeSidePart. */
  private async installSideParts(
    loadedSideParts: LoadedSidePart[],
  ): Promise<void> {
    const generation = ++this._sidePartsInstallGeneration;

    let materials: LoadedMaterial[] | undefined;
    if (this._materialConfig.size > 0) {
      materials = await this.loadMaterials(this._materialConfig);
      if (generation !== this._sidePartsInstallGeneration) return;
      this.applyLoadedMaterials(
        materials,
        ...loadedSideParts.map((part) => part.sideModel),
      );
    }

    await this.alignSidePartModels(loadedSideParts);
    if (generation !== this._sidePartsInstallGeneration) return;

    this.recomputeBound();
  }

  /** Clear + reload kolçaklar onto current empty joints. */
  async refreshSideParts(): Promise<void> {
    this.clearSideParts();
    if (this._sideConfig.size < 1) {
      this._sideModels = [];
      this.recomputeBound();
      return;
    }
    await this.installSideParts(await this.loadSidePartModels(this._sideConfig));
  }

  private _sidePartsInstallGeneration = 0;

  private _materialConfig: Map<string, { code: string; material: any }> =
    new Map();
  private _stateRestore = Promise.resolve();

  private _configData?: CONFIGURATOR_MODEL_TYPE;

  get configData(): CONFIGURATOR_MODEL_TYPE {
    return this._configData ?? CONFIGURATOR_DATA;
  }

  constructor(
    mainConfig: Map<string, MODEL_PART_TYPE>,
    sideConfig: Map<string, MODEL_PART_TYPE>,
    materialConfig: Map<string, { code: string; material: any }>,
    configData?: CONFIGURATOR_MODEL_TYPE,
  ) {
    super({});
    this.name = "Configurator";
    this._mainConfig = mainConfig;
    this._sideConfig = sideConfig;
    this._materialConfig = materialConfig;
    this._configData = configData;
  }

  override get state(): SugarStateSlice<ConfiguratorStateValue> {
    const modelState = super.state;
    return {
      kind: "sugarModelConfigurator",
      value: {
        model: modelState.value,
        mainConfig: Array.from(this._mainConfig?.entries() ?? []),
        sideConfig: Array.from(this._sideConfig?.entries() ?? []),
        materialConfig: Array.from(this._materialConfig?.entries() ?? []),
      },
    };
  }

  override applyState(slice: SugarStateSlice, dir: HistoryDir): void {
    if (slice.kind !== "sugarModelConfigurator") return;
    const value = slice.value as Partial<ConfiguratorStateValue>;
    super.applyState({ kind: "sugarModel", value: value.model ?? {} }, dir);
    const mainConfig = new Map(value.mainConfig ?? []);
    const sideConfig = new Map(value.sideConfig ?? []);
    const materialConfig = new Map(value.materialConfig ?? []);
    this._stateRestore = this._stateRestore
      .catch(() => undefined)
      .then(async () => {
        this._mainConfig = mainConfig;
        this._sideConfig = sideConfig;
        this._materialConfig = materialConfig;
        await this.configureWithPreloadedAssets();
      });
  }

  whenStateSettled(): Promise<void> {
    return this._stateRestore;
  }

  private _collisionModels(): SugarModel[] {
    const seen = new Set<string>();
    const models = [
      ...this._models,
      ...this._sideModels,
      ...this._events.flatMap((event) => event.models),
    ];

    return models.filter((model) => {
      const key = model.uuid ?? String(model.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  override getCollisionLocalObbs(): OBB[] {
    this.updateMatrixWorld(true);
    const toConfiguratorLocal = new Matrix4().copy(this.matrixWorld).invert();
    const childToConfigurator = new Matrix4();
    const obbs: OBB[] = [];

    for (const model of this._collisionModels()) {
      model.updateMatrixWorld(true);
      childToConfigurator.multiplyMatrices(
        toConfiguratorLocal,
        model.matrixWorld,
      );

      for (const localObb of model.getCollisionLocalObbs()) {
        const obb = transformObb(localObb, childToConfigurator);
        (obb as any).model = (localObb as any).model ?? model;
        (obb as any).sourceModel = model;
        (obb as any).configurator = this;
        obbs.push(obb);
      }
    }

    return obbs.length > 0 ? obbs : super.getCollisionLocalObbs();
  }

  override getCollisionLocalObb(): OBB {
    const obbs = this.getCollisionLocalObbs();
    if (obbs.length === 0) return super.getCollisionLocalObb();

    const box = new Box3();
    for (const obb of obbs) {
      box.expandByPoint(obbCorner(obb, -1, -1, -1));
      box.expandByPoint(obbCorner(obb, -1, -1, 1));
      box.expandByPoint(obbCorner(obb, -1, 1, -1));
      box.expandByPoint(obbCorner(obb, -1, 1, 1));
      box.expandByPoint(obbCorner(obb, 1, -1, -1));
      box.expandByPoint(obbCorner(obb, 1, -1, 1));
      box.expandByPoint(obbCorner(obb, 1, 1, -1));
      box.expandByPoint(obbCorner(obb, 1, 1, 1));
    }

    if (box.isEmpty()) return super.getCollisionLocalObb();

    const broad = new OBB();
    const size = new Vector3();
    box.getCenter(broad.center);
    box.getSize(size);
    broad.halfSize.copy(size.multiplyScalar(0.5));
    (broad as any).model = this;
    return broad;
  }

  override getCollisionWorldObbs(): OBB[] {
    this.updateMatrixWorld(true);
    const out: OBB[] = [];
    for (const model of this._collisionModels()) {
      model.updateMatrixWorld(true);
      for (const worldObb of model.getCollisionWorldObbs()) {
        (worldObb as any).sourceModel = model;
        (worldObb as any).configurator = this;
        out.push(worldObb);
      }
    }
    return out;
  }

  /** No own `_model` — bound from child modules. */
  override recomputeBound(): void {
    this.updateMatrixWorld(true);
    const localObbs = this.getCollisionLocalObbs();
    this._localObbs = localObbs;
    const broad = this.getCollisionLocalObb();
    this._localObb.copy(broad);
    (this._localObb as any).model = this;

    if (localObbs.length < 1) {
      this._bound = undefined;
      return;
    }
    const box = new Box3();
    for (const sx of [-1, 1] as const) {
      for (const sy of [-1, 1] as const) {
        for (const sz of [-1, 1] as const) {
          box.expandByPoint(obbCorner(broad, sx, sy, sz));
        }
      }
    }
    this._bound = box;
  }

  groupConnectedSugarModels(
    models: SugarModel[],
    events: ConfiguratorEvent[],
  ): SugarModel[][] {
    const idToModel = new Map<number, SugarModel>();
    const adjacency = new Map<number, Set<number>>();

    for (const model of models) {
      idToModel.set(model.id, model);
      adjacency.set(model.id, new Set());
    }

    for (const event of events) {
      const a = event.baseJoint.model.id;
      const b = event.tailJoint.model.id;

      adjacency.get(a)?.add(b);
      adjacency.get(b)?.add(a);
    }

    const visited = new Set<number>();
    const groups: SugarModel[][] = [];

    for (const model of models) {
      if (visited.has(model.id)) continue;

      const group: SugarModel[] = [];
      const stack = [model.id];

      while (stack.length > 0) {
        const currentId = stack.pop()!;
        if (visited.has(currentId)) continue;

        visited.add(currentId);
        const currentModel = idToModel.get(currentId);
        if (currentModel) group.push(currentModel);

        for (const neighborId of adjacency.get(currentId) ?? []) {
          if (!visited.has(neighborId)) {
            stack.push(neighborId);
          }
        }
      }

      groups.push(group);
    }

    return groups;
  }

  get products() {
    const groupedModels = this.groupConnectedSugarModels(
      this._models,
      this._events,
    );

    const result = [
      ...groupedModels.map((models) =>
        models
          .map((model) => [
            model,
            ...(model.joints
              ?.filter((j) => j.freeze && j.bindedJoint && j.visibility)
              .map((j) => j.bindedJoint?.model) || []),
          ])
          .flat(),
      ),
      this._sideModels,
      ...this._events.map((e) => e.models),
      //this._legModels || [],
    ];

    return result;
  }

  get models() {
    return this._models;
  }

  get joints() {
    return this._models.flatMap((model) => model.joints || []);
  }

  private _mergeHappened:
    | {
        distance: number;
        base: any;
        tail: any;
      }
    | undefined;

  private _merging = false;

  addModel(addModel: SugarModel) {
    this.addModelWithoutComplete(addModel);
    this.complete();
  }

  addModelWithoutComplete(addModel: SugarModel, alignDistance: number = 1) {
    this._mergeHappened = fetchMinDistance(addModel, [this]);
    const minModel = this._mergeHappened;
    if (minModel && minModel.distance < alignDistance) {
      alignJoints(minModel!.base, minModel!.tail);
      minModel.base.bindJoint(minModel.tail);
    }

    this._attachAddedModel(addModel, minModel);
    this.__addModel(addModel);
  }

  /** Body layout v2 kök node — joint eşlemesi olmadan configurator'a ekler. */
  addRootModel(addModel: SugarModel) {
    this.attach(addModel);
    this.__addModel(addModel);
  }

  /**
   * Body layout v2 — child.outType parent.inType ile explicit bağlar.
   * bindModelsByJointSpec içinde alignJoints → bindJoint (attach öncesi).
   * @returns false joint bulunamadıysa
   */
  addModelWithExplicitJoint(
    parentModel: SugarModel,
    childModel: SugarModel,
    spec: { childOut: string; parentIn: string },
  ): boolean {
    parentModel.updateWorldMatrix(true, true);
    childModel.updateWorldMatrix(true, true);

    const bound = bindModelsByJointSpec(parentModel, childModel, spec);
    if (!bound) {
      return false;
    }

    if (childModel.userData.info?.replaceType == 1) {
      bound.base.model.attach(childModel);
    } else {
      this.attach(childModel);
    }

    //realignJoints(childModel, bound.tail);
    this.__addModel(childModel);
    return true;
  }

  private _attachAddedModel(
    addModel: SugarModel,
    minModel:
      | { distance: number; base: SugarModelJoint; tail: SugarModelJoint }
      | undefined,
  ) {
    if (
      minModel &&
      minModel.distance < 1 &&
      addModel.userData.info?.replaceType == 1
    ) {
      minModel.base.model.attach(addModel);
    } else {
      this.attach(addModel);
    }
  }

  async completeSync() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    await this.configureWithPreloadedAssets();
  }

  complete() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    void this.configureWithPreloadedAssets();

    //this.haveJoints();
  }

  private async preloadAssets(): Promise<PreloadedAssets> {
    const [, sideModels, materials, mergeEvents] = await Promise.all([
      this.loadMainPart(this._mainConfig),
      this.loadSidePartModels(this._sideConfig),
      this.loadMaterials(this._materialConfig),
      this.loadMergeEvents(),
    ]);

    return {
      sideModels,
      materials,
      mergeEvents,
    };
  }

  private async configureWithPreloadedAssets() {
    const assets = await this.preloadAssets();

    await this.mergePossibleJoints(assets.mergeEvents);
    await this.alignSidePartModels(assets.sideModels);
    await this.mergePendingJoint(assets.mergeEvents);
    assets.mergeEvents.forEach((event) => this.applyMergeEvent(event));

    this.applyLoadedMaterials(assets.materials, ...this._models);
    this.applyLoadedMaterials(assets.materials, ...this._sideModels);
    this.applyLoadedMaterials(
      assets.materials,
      ...this._events.map((e) => e.models).flat(),
    );

    this.recomputeBound();
    this._syncBoundDebug();
    this.dispatchEvent({ type: "configured" as any });
    logConfiguratorSlotState(`complete:${this.uuid.slice(0, 8)}`, [this]);
  }

  private _syncBoundDebug() {
    if (this._debugRafId != null) {
      cancelAnimationFrame(this._debugRafId);
      this._debugRafId = undefined;
    }
    this._childObbDebug.forEach((m) => {
      m.removeFromParent();
      (m.geometry as any)?.dispose?.();
      (m.material as any)?.dispose?.();
    });
    this._childObbDebug = [];

    if (this._boundDebug) {
      this._boundDebug.removeFromParent();
      (this._boundDebug.geometry as any)?.dispose?.();
      (this._boundDebug.material as any)?.dispose?.();
      this._boundDebug = undefined;
    }

    if (this._centerCylDebug) {
      this._centerCylDebug.removeFromParent();
      (this._centerCylDebug.geometry as any)?.dispose?.();
      (this._centerCylDebug.material as any)?.dispose?.();
      this._centerCylDebug = undefined;
    }

    this._jointsDebug.forEach((j) => {
      j.removeFromParent();
      (j.geometry as any)?.dispose?.();
      (j.material as any)?.dispose?.();
    });
    this._jointsDebug = [];

    if (isObbDebugDrawEnabled()) {
      let sceneRoot: any = this.parent;
      while (sceneRoot && sceneRoot.parent) sceneRoot = sceneRoot.parent;
      const debugParent = sceneRoot ?? this;

      const cylGeo = new CylinderGeometry(0.05, 0.05, 20, 16);
      const cylMat = new MeshBasicMaterial({
        color: 0xff0000,
        depthTest: false,
      });
      const cyl = new Mesh(cylGeo, cylMat);
      cyl.renderOrder = 9999;
      cyl.frustumCulled = false;
      cyl.name = "configurator-center-cyl";
      this._centerCylDebug = cyl;
      debugParent.add(cyl);

      this._jointsDebug = this.joints.map((j) => {
        const cgeo = new SphereGeometry(1, 16, 16);
        const cmat = new MeshBasicMaterial({
          color: 0x00aaff,
          depthTest: false,
        });
        const frontMat = new MeshBasicMaterial({
          color: 0x00aaff,
          depthTest: false,
        });
        const rightMat = new MeshBasicMaterial({
          color: 0xaa00ff,
          depthTest: false,
        });
        const csphere = new Mesh(cgeo, cmat);

        const front = new CylinderGeometry(0.1, 0.1, 10.0, 16);
        front.rotateZ(Math.PI / 2);
        const right = new CylinderGeometry(0.1, 0.1, 10.0, 16);
        right.rotateX(Math.PI / 2);

        csphere.frustumCulled = false;
        csphere.renderOrder = 9998;
        csphere.scale.set(0.1, 0.1, 0.1);
        csphere.name = `joint-${j.model.id}-${j.jointId}`;
        j.model.updateWorldMatrix(true, true);
        csphere.add(new Mesh(front, frontMat));
        csphere.add(new Mesh(right, rightMat));
        debugParent.add(csphere);
        return csphere;
      });

      const worldObbs = this.getCollisionWorldObbs();
      for (let i = 0; i < worldObbs.length; i++) {
        const cgeo = new EdgesGeometry(new BoxGeometry(1, 1, 1));
        const cmat = new LineBasicMaterial({
          color: 0x00aaff,
          depthTest: false,
        });
        const cwire = new LineSegments(cgeo, cmat);
        cwire.frustumCulled = false;
        cwire.renderOrder = 9998;
        debugParent.add(cwire);
        this._childObbDebug.push(cwire);
      }

      const updateLoop = () => {
        if (!this._childObbDebug.length && !this._centerCylDebug) return;
        const obbs = this.getCollisionWorldObbs();
        const n = Math.min(obbs.length, this._childObbDebug.length);

        for (let i = 0; i < n; i++) {
          const o = obbs[i];
          const w = this._childObbDebug[i];
          w.position.copy(o.center);
          const m4 = new Matrix4().setFromMatrix3(o.rotation);
          w.quaternion.setFromRotationMatrix(m4);
          const s = o.halfSize.clone().multiplyScalar(2);
          w.scale.set(
            Math.max(s.x, 1e-6),
            Math.max(s.y, 1e-6),
            Math.max(s.z, 1e-6),
          );
        }

        for (let i = 0; i < this.joints.length; i++) {
          const j = this.joints[i];
          const jd = this._jointsDebug[i];
          const worldPosition = new Vector3();
          j.updateWorldMatrix(true, true);
          j.getWorldPosition(worldPosition);
          jd.position.copy(worldPosition);
          jd.name = `joint-${j.model.id}-${j.jointId}`;
        }

        if (this._centerCylDebug) {
          this.updateMatrixWorld(true);
          this._centerCylDebug.position.setFromMatrixPosition(this.matrixWorld);
        }
        this._debugRafId = requestAnimationFrame(updateLoop);
      };
      if (this._debugRafId != null) cancelAnimationFrame(this._debugRafId);
      this._debugRafId = requestAnimationFrame(updateLoop);
    }
  }

  grabModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    //this.possesJoints();
  }

  private isSameJoint(a: SugarModelJoint, b: SugarModelJoint) {
    return (
      a === b ||
      (a.model === b.model &&
        a.jointId === b.jointId &&
        a.inType === b.inType &&
        a.outType === b.outType)
    );
  }

  private isSameMergeEvent(
    event: ConfiguratorEvent,
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    return (
      (this.isSameJoint(event.baseJoint, baseJoint) &&
        this.isSameJoint(event.tailJoint, tailJoint)) ||
      (this.isSameJoint(event.tailJoint, baseJoint) &&
        this.isSameJoint(event.baseJoint, tailJoint))
    );
  }

  private mergeEventKey(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    const keyOf = (joint: SugarModelJoint) =>
      [joint.model?.uuid, joint.jointId, joint.inType, joint.outType].join(":");
    return [keyOf(baseJoint), keyOf(tailJoint)].sort().join("|");
  }

  private hasMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    return this._events.find((e) =>
      this.isSameMergeEvent(e, baseJoint, tailJoint),
    );
  }

  private resolveJoint(joint: SugarModelJoint) {
    return (
      joint.model?.joints?.find((candidate) =>
        this.isSameJoint(candidate, joint),
      ) ?? joint
    );
  }

  private getMergeEventDefinition(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ) {
    const baseId = baseJoint.model.userData.info.id;
    const tailId = tailJoint.model.userData.info.id;

    const items = [baseId, tailId].sort((a, b) => a - b);
    return this.configData.configurator.events?.find(
      (e) =>
        e.when.type === "merged" &&
        e.when.items.length === items.length &&
        [...e.when.items].sort().join(",") === [...items].sort().join(","),
    );
  }

  private async loadMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
  ): Promise<ConfiguratorEvent | undefined> {
    if (this.hasMergeEvent(baseJoint, tailJoint)) return;

    const key = this.mergeEventKey(baseJoint, tailJoint);
    const cached = this._mergeEventLoadCache.get(key);
    if (cached) return cached;

    const event = this.getMergeEventDefinition(baseJoint, tailJoint);
    if (!event) return;

    const loadPromise = (async () => {
      const operations = await Promise.all(
        (event.operations ?? []).map(async (operation) => {
          if (operation.action === "add") {
            const type = operation.parameters?.type!;
            const modelId = this._mainConfig
              .get(type)
              ?.joints?.find((j) => j.type === type)?.match;
            const modelData = this.configData.models.find(
              (m) => m.id == modelId,
            );
            const modelObject = modelData
              ? await new SugarModel(modelData).load(modelData, {
                  forceSync: true,
                })
              : undefined;

            return {
              return: modelObject,
              parameters: { base: baseJoint, tail: tailJoint },
              operation: operation,
            };
          }

          return {
            return: undefined,
            parameters: undefined,
            operation: operation,
          };
        }),
      );

      return {
        baseJoint,
        tailJoint,
        operations,
        models: operations
          .map((operation) => operation.return)
          .filter((model): model is SugarModel => !!model),
      };
    })();

    this._mergeEventLoadCache.set(key, loadPromise);
    return loadPromise;
  }

  private findLoadedMergeEvent(
    baseJoint: SugarModelJoint,
    tailJoint: SugarModelJoint,
    mergeEvents: ConfiguratorEvent[],
  ) {
    return mergeEvents.find((event) =>
      this.isSameMergeEvent(event, baseJoint, tailJoint),
    );
  }

  private getBoundPrimaryJointPairs(): Array<{
    baseJoint: SugarModelJoint;
    tailJoint: SugarModelJoint;
  }> {
    const primaryModels = new Set(this._models);
    const pairs = new Map<
      string,
      { baseJoint: SugarModelJoint; tailJoint: SugarModelJoint }
    >();

    for (const model of this._models) {
      for (const joint of model.joints ?? []) {
        const boundJoint = joint.bindedJoint;
        if (
          !boundJoint ||
          boundJoint === joint ||
          !primaryModels.has(boundJoint.model)
        ) {
          continue;
        }

        const pair = { baseJoint: joint, tailJoint: boundJoint };
        const key = this.mergeEventKey(pair.baseJoint, pair.tailJoint);
        if (!pairs.has(key)) pairs.set(key, pair);
      }
    }

    return Array.from(pairs.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([, pair]) => pair);
  }

  private async loadMergeEvents(): Promise<ConfiguratorEvent[]> {
    const pairs = [
      ...this.getBoundPrimaryJointPairs(),
      ...(this._unbindedJoints.length > 1
        ? [
            {
              baseJoint: this._unbindedJoints[0],
              tailJoint: this._unbindedJoints[1],
            },
          ]
        : []),
      ...(this._mergeHappened && !this._merging
        ? [
            {
              baseJoint: this._mergeHappened.base,
              tailJoint: this._mergeHappened.tail,
            },
          ]
        : []),
    ];
    const uniquePairs = Array.from(
      new Map(
        pairs.map((pair) => [
          this.mergeEventKey(pair.baseJoint, pair.tailJoint),
          pair,
        ]),
      ).values(),
    );

    return (
      await Promise.all(
        uniquePairs.map((pair) =>
          this.loadMergeEvent(pair.baseJoint, pair.tailJoint),
        ),
      )
    ).filter((event): event is ConfiguratorEvent => !!event);
  }

  private applyMergeEvent(eventInstance: ConfiguratorEvent | undefined) {
    if (!eventInstance) return;

    const baseJoint = this.resolveJoint(eventInstance.baseJoint);
    const tailJoint = this.resolveJoint(eventInstance.tailJoint);

    if (this.hasMergeEvent(baseJoint, tailJoint)) return;
    this._mergeEventLoadCache.delete(this.mergeEventKey(baseJoint, tailJoint));

    const baseType = baseJoint.outType;
    const models: SugarModel[] = [];

    eventInstance.operations?.forEach((operationItem) => {
      if (operationItem.operation.action === "visible") {
        const baseJoints = baseJoint.model.joints?.filter(
          (j) => j.tags.includes(baseType) && j.bindedJoint,
        );
        baseJoints?.forEach((j) => (j.visibility = false));

        const tailType = tailJoint.outType;
        const tailJoints = tailJoint.model.joints?.filter(
          (j) => j.tags.includes(tailType) && j.bindedJoint,
        );
        tailJoints?.forEach((j) => (j.visibility = false));
      } else if (operationItem.operation.action === "add") {
        const modelObject = operationItem.return as SugarModel | undefined;
        if (!modelObject) return;

        const basePosition = new Vector3();
        baseJoint.updateWorldMatrix(true, true);
        baseJoint.model.getWorldPosition(basePosition);

        const tailPosition = new Vector3();
        tailJoint.updateWorldMatrix(true, true);
        tailJoint.model.getWorldPosition(tailPosition);

        const centerPosition = basePosition
          .clone()
          .add(tailPosition)
          .multiplyScalar(0.5);

        modelObject.position.copy(centerPosition);
        modelObject.position.setY(0);

        if (operationItem.operation.parameters?.offset) {
          modelObject.position.setX(
            modelObject.position.x +
              operationItem.operation.parameters.offset[0],
          );
          modelObject.position.setY(
            modelObject.position.y +
              operationItem.operation.parameters.offset[1],
          );
          modelObject.position.setZ(
            modelObject.position.z +
              operationItem.operation.parameters.offset[2],
          );
        }

        baseJoint.model.attach(modelObject);
        models.push(modelObject);
      }
    });

    this._events.push({
      baseJoint,
      tailJoint,
      operations: eventInstance.operations,
      models,
    });
  }

  private reposition() {
    const mainModels = this._models?.filter(
      (m) => m.userData.info?.replaceType == 0,
    );
    const models = [
      ...(mainModels.length > 0 ? mainModels : this._models),
      ...this._sideModels,
    ];
    if (models.length < 1) return;

    const worldBox = new Box3();
    models.forEach((m) => {
      m.updateMatrixWorld(true);
      for (const obb of m.getCollisionWorldObbs()) {
        worldBox.expandByPoint(obbCorner(obb, -1, -1, -1));
        worldBox.expandByPoint(obbCorner(obb, -1, -1, 1));
        worldBox.expandByPoint(obbCorner(obb, -1, 1, -1));
        worldBox.expandByPoint(obbCorner(obb, -1, 1, 1));
        worldBox.expandByPoint(obbCorner(obb, 1, -1, -1));
        worldBox.expandByPoint(obbCorner(obb, 1, -1, 1));
        worldBox.expandByPoint(obbCorner(obb, 1, 1, -1));
        worldBox.expandByPoint(obbCorner(obb, 1, 1, 1));
      }
    });

    models.forEach((m) => m.detach());

    if (!worldBox.isEmpty()) {
      const worldCenter = new Vector3();
      worldBox.getCenter(worldCenter);
      worldCenter.y = this.position.y;
      this.position.copy(worldCenter);
      this.updateWorldMatrix(true, true);
    }

    models.forEach((m) => this.attach(m));
  }

  private havePossibleJoints(model: SugarModel) {
    this._unbindedJoints = getPossibleBindableJoints(model);
    //this._unbindedJoints?.forEach((joint) => joint.unbindJoint());
  }

  private async mergePossibleJoints(mergeEvents: ConfiguratorEvent[] = []) {
    if (this._unbindedJoints.length > 1) {
      const baseJoint = this.resolveJoint(this._unbindedJoints[0]);
      const tailJoint = this.resolveJoint(this._unbindedJoints[1]);

      // alignJoints / applyJointSnapDelta world translation → model.position.
      // Under a rotated/wall parent that corrupts leftover join; scene-root first
      // (same idea as realignAndReposition).
      const toReattach = this._models.filter((m) => m.parent === this);
      for (const m of toReattach) m.detach();
      for (const m of this._models) m.updateWorldMatrix(true, false);

      alignJoints(baseJoint, tailJoint);
      baseJoint.bindJoint(tailJoint);

      realignJoints(tailJoint.model as SugarModel, tailJoint);

      for (const m of toReattach) this.attach(m);
      this.updateWorldMatrix(true, true);

      const eventInstance = this.findLoadedMergeEvent(
        this._unbindedJoints[0],
        this._unbindedJoints[1],
        mergeEvents,
      );
      this.applyMergeEvent(eventInstance);
    }

    this._unbindedJoints = [];
  }

  private async mergePendingJoint(mergeEvents: ConfiguratorEvent[] = []) {
    if (!this._mergeHappened || this._merging) return;

    this._merging = true;
    try {
      const eventInstance = this.findLoadedMergeEvent(
        this._mergeHappened.base,
        this._mergeHappened.tail,
        mergeEvents,
      );

      this.applyMergeEvent(eventInstance);
      this._mergeHappened = undefined;
    } finally {
      this._merging = false;
    }
  }

  private _dirty = true;
  private __addModel(model: SugarModel) {
    model.configurator = this;
    if (!this._models.includes(model)) this._models.push(model);
    //this.possesJoints();
    this._dirty = true;
  }

  private __removeModel(model: SugarModel) {
    this._models = this._models.filter((m) => m != model);

    model.joints
      ?.filter((j) => j && j.bindedJoint)
      .forEach((j) => {
        this.unbindEvents(j, j.bindedJoint!);
      });

    model.joints?.forEach((j) => {
      // Clear every non-freeze bind — join uses outType↔inType; filtering only
      // inType left outType slots occupied and blocked re-join after detach.
      if (j.model == model && j.binded && !j.freeze) {
        j.unbindJoint();
      }
      j.model = model;
    });

    model.configurator = undefined;
    model.detach();
    this._dirty = true;
  }

  private unbindEvents(baseJoint: SugarModelJoint, tailJoint: SugarModelJoint) {
    const eventInstance = this._events.find(
      (e) =>
        (e.baseJoint == baseJoint && e.tailJoint == tailJoint) ||
        (e.tailJoint == baseJoint && e.baseJoint == tailJoint),
    );

    if (eventInstance) {
      eventInstance.operations?.forEach((operationItem) => {
        if (operationItem.operation.action === "visible") {
          const baseType = baseJoint.outType;

          console.log("visible açılıyor", {
            baseType: baseType,
            baseJoint: baseJoint,
            tailJoint: tailJoint,
          });
          const baseJoints = baseJoint.model.joints?.filter(
            (j) => j.tags.includes(baseType) && j.bindedJoint,
          );
          baseJoints?.forEach((j) => (j.visibility = true));

          const tailType = tailJoint.outType;
          const tailJoints = tailJoint.model.joints?.filter(
            (j) => j.tags.includes(tailType) && j.bindedJoint,
          );
          tailJoints?.forEach((j) => (j.visibility = true));
        } else if (operationItem.operation.action === "add") {
          //
          operationItem.return?.removeFromParent();
        }
      });
      eventInstance.operations = [];
    }

    eventInstance?.models.forEach((model) => {
      model.removeFromParent();
    });

    this._events = this._events.filter((e) => e != eventInstance);
  }

  removeModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    this.complete();
    this.reposition();
  }

  realignAndReposition() {
    this._dirty = false;

    const children = [...this._models, ...this._sideModels];
    children.forEach((m) => m.detach());

    void this.mergePossibleJoints([]).then(() => {
      children.forEach((m) => this.attach(m));
      this.reposition();
      this.recomputeBound();
      this._syncBoundDebug();
      this.dispatchEvent({ type: "configured" as any });
    });
  }

  /**
   *
   * @param Materials
   *
   *
   */

  private async loadMaterials(
    materialConfig: Map<string, { code: string; material: any }>,
  ): Promise<LoadedMaterial[]> {
    this._materialConfig = materialConfig;
    const materials = await Promise.all(
      Array.from(materialConfig?.values() ?? []).map(async (materialConf) => {
        if (!materialConf.material) {
          logger.warn("material is undefined", materialConf.code);
          return;
        }
        const material = createMaterial(materialConf.material);
        await material.load();

        return {
          code: materialConf.code,
          material,
        };
      }),
    );

    return materials.filter(
      (material): material is LoadedMaterial => !!material,
    );
  }

  private applyLoadedMaterials(
    loadedMaterials: LoadedMaterial[],
    ...inputModels: SugarModel[]
  ) {
    const models =
      inputModels.length > 0
        ? inputModels
        : [
            ...this._models,
            ...this._sideModels,
            ...this._events.map((e) => e.models),
          ].flat();

    loadedMaterials.forEach((materialConf) => {
      const codes = materialConf.code.split(";");
      models?.forEach((model) => {
        model.partMaterialGroups?.forEach((part) => {
          if (codes.includes(part.code)) {
            part.apply(model, materialConf.material);
          }
        });

        model.joints?.forEach((joint) => {
          const jointModel = joint.bindedJoint?.model;
          if (jointModel && model.hasIt(jointModel!)) {
            jointModel?.partMaterialGroups?.forEach((part) => {
              if (codes.includes(part.code)) {
                part.apply(jointModel, materialConf.material);
              }
            });
          }
        });
      });
    });
  }

  async applyMaterial(
    materialConfig: Map<string, { code: string; material: any }>,
    ...inputModels: SugarModel[]
  ) {
    const materials = await this.loadMaterials(materialConfig);
    this.applyLoadedMaterials(materials, ...inputModels);
    return materials;
  }

  private _getEmptyJoints(): SideJointSlot[] {
    return Array.from(this._sideConfig.values())
      .filter((value) => value !== undefined)
      .flatMap((value) => {
        return value.joints.flatMap((jointdata) => {
          return this._models.flatMap((model) =>
            model
              .joints!.filter(
                (joint) =>
                  joint.inType === jointdata.type &&
                  !joint.binded &&
                  !joint.model?.userData?.info?.jointInfo?.excludes?.includes(
                    jointdata.id,
                  ),
              )
              .map((joint) => {
                return {
                  joint: joint,
                  data: jointdata,
                };
              }),
          );
        });
      });
  }

  private getSideModelConfig(jointData: JOINT_TYPE) {
    if (typeof jointData.match !== "number") {
      logger.warn(
        "[SugarModelConfigurator.changeSidePart] side joint match number değil",
        {
          match: jointData.match,
          type: jointData.type,
        },
      );
      return undefined;
    }

    return this.configData.models.find((model) => model.id === jointData.match);
  }

  private async loadSideModelForSlot(
    emptyJointSlot: SideJointSlot,
    useCache = true,
  ): Promise<LoadedSidePart | null> {
    const modelConfig = this.getSideModelConfig(emptyJointSlot.data);

    if (!modelConfig) {
      logger.warn("[SugarModelConfigurator.changeSidePart] modelConfig yok", {
        emptyJointSlotMatch: emptyJointSlot.data.match,
        emptyJointType: emptyJointSlot.data.type,
      });
      return null;
    }

    let sideModel = useCache
      ? this._sideModels.find(
          (cachedModel) => cachedModel.data.id === modelConfig.id,
        )
      : undefined;

    if (!sideModel) {
      sideModel = await new SugarModel(modelConfig).load(modelConfig, {
        forceSync: true,
      });
    }

    if (!sideModel) {
      logger.warn(
        "[SugarModelConfigurator.changeSidePart] sideModel yüklenemedi",
        {
          modelConfigId: modelConfig.id,
        },
      );
      return null;
    }

    return {
      data: emptyJointSlot.data,
      sideModel,
    };
  }

  private async loadSidePartModels(
    sideConfig: Map<string, MODEL_PART_TYPE>,
  ): Promise<LoadedSidePart[]> {
    this._sideConfig = sideConfig;
    //console.log("changeSidePart", sideConfig);
    logger.info("changeSidePart", sideConfig);

    const emptyjoints = this._getEmptyJoints();

    return (
      await Promise.all(
        emptyjoints.map((emptyJointSlot) =>
          this.loadSideModelForSlot(emptyJointSlot),
        ),
      )
    ).filter((m): m is LoadedSidePart => m != null);
  }

  private alignAndAttachSidePart(
    emptyJointSlot: SideJointSlot,
    sideModel: SugarModel,
  ): boolean {
    const modelConfig = this.getSideModelConfig(emptyJointSlot.data);
    const tailJoint = sideModel.joints?.find(
      (j) => j.outType == emptyJointSlot.data.type,
    );

    if (!tailJoint) {
      logger.warn(
        "[SugarModelConfigurator.changeSidePart] tailJoint bulunamadı (alignJoints atlanıyor)",
        {
          beklenenOutType: emptyJointSlot.data.type,
          modelId: modelConfig?.id,
          modelName: modelConfig?.name,
          mevcutJointOutTypes: sideModel.joints?.map((j) => j.outType) ?? [],
          baseJoint: {
            inType: emptyJointSlot.joint.inType,
            outType: emptyJointSlot.joint.outType,
          },
        },
      );
      return false;
    }

    sideModel.detach();
    sideModel.removeFromParent();
    sideModel.position.set(0, 0, 0);
    sideModel.rotation.set(0, 0, 0);
    sideModel.updateWorldMatrix(true, true);
    alignJoints(emptyJointSlot.joint, tailJoint, true);
    this.attach(sideModel);
    tailJoint.model.freeze = true;
    return true;
  }

  private async alignSidePartModels(loadedSideParts: LoadedSidePart[]) {
    this._sideModels.forEach((part) => {
      part?.removeFromParent();
    });

    const emptyjoints = this._getEmptyJoints();
    const usedLoadedIndexes = new Set<number>();
    const alignedModels: SugarModel[] = [];

    for (const emptyJointSlot of emptyjoints) {
      let loadedIndex = loadedSideParts.findIndex(
        (loadedSidePart, index) =>
          !usedLoadedIndexes.has(index) &&
          loadedSidePart.data.type === emptyJointSlot.data.type &&
          loadedSidePart.data.match === emptyJointSlot.data.match,
      );

      let loadedSidePart =
        loadedIndex >= 0 ? loadedSideParts[loadedIndex] : undefined;

      if (!loadedSidePart) {
        const fallbackSidePart = await this.loadSideModelForSlot(
          emptyJointSlot,
          false,
        );
        loadedIndex = loadedSideParts.length;
        if (fallbackSidePart) {
          loadedSidePart = fallbackSidePart;
          loadedSideParts.push(fallbackSidePart);
        }
      }

      if (!loadedSidePart) continue;
      usedLoadedIndexes.add(loadedIndex);

      if (this.alignAndAttachSidePart(emptyJointSlot, loadedSidePart.sideModel)) {
        alignedModels.push(loadedSidePart.sideModel);
      }
    }

    this._sideModels = alignedModels;
  }

  async changeSidePart(sideConfig: Map<string, MODEL_PART_TYPE>) {
    await this.installSideParts(await this.loadSidePartModels(sideConfig));
  }

  private async loadMainPart(partConfig: Map<string, MODEL_PART_TYPE>) {
    const allModels = this._models;
    this._mainConfig = partConfig;

    await Promise.all([
      ...allModels
        .filter((m) => m.userData.info?.replaceType === 0)
        .map(async (m) => {
          await (m as SugarModelGroup).load({
            data: m.data,
            partConfig: partConfig,
          });
        }),
      ...this._events
        .map((e) => e.models)
        .flat()
        .map(async (m) => {
          await (m as SugarModel).load(
            this.configData.models.find(
              (m) => m.id == getPartTypeMatch(partConfig.get("legIn")!, 0),
            ),
            {
              forceSync: true,
              forceAgain: true,
            },
          );
        }),
    ]);
  }

  async changeMainPart(partConfig: Map<string, MODEL_PART_TYPE>) {
    await this.loadMainPart(partConfig);
    if (this._sideConfig.size > 0) {
      await this.changeSidePart(this._sideConfig);
    }
  }

  override dispose(): void {
    for (const model of this._models) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._models = [];
    for (const model of this._sideModels) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._sideModels = [];
    disposeObject3DDeep(this);
    super.dispose();
  }
}
